import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BrowserQRCodeReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import type { EntidadQR } from '@cmms/shared';
import { api, ErrorApi } from '../servicios/api.js';
import { Aviso, Dialogo, useCierreDialogo } from './piezas.js';
import { IconoCerrar, IconoLinterna, IconoQr } from './iconos.js';
import { useEsMovil } from '../servicios/responsive.js';

/**
 * Cámara y escaneo de QR para el técnico en campo (§16.2/§16.3).
 *
 * Todo en web: `getUserMedia` para el vídeo en vivo y `@zxing/browser` para
 * decodificar el QR, sin plugins nativos de Capacitor. El WebView de la app
 * concede el permiso de cámara si el manifiesto/plist lo declara; en el
 * navegador de escritorio funciona igual con webcam.
 */

/** ¿Hay una cámara accesible por `getUserMedia`? El botón de galería siempre queda. */
export function hayCamara(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

// ─── Captura de foto ────────────────────────────────────────────────────────

/**
 * Modal con la cámara en vivo y un botón de captura. Entrega un `File` JPEG por
 * `alCapturar` — el mismo tipo que da `<input type="file">`, así que quien lo
 * use lo sube por la ruta que ya tiene (`api.subirArchivo('/adjuntos', …)`).
 */
export function CapturaFoto({
  alCapturar,
  alCerrar,
}: {
  alCapturar: (archivo: File) => void;
  alCerrar: () => void;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => undefined);
        }
        setListo(true);
      })
      .catch(() => {
        if (!cancelado) setError('No se pudo abrir la cámara. Revise los permisos del navegador.');
      });
    return () => {
      cancelado = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function capturar(): void {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('No se pudo procesar la foto.');
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('No se pudo procesar la foto.');
          return;
        }
        alCapturar(new File([blob], `captura-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  }

  return (
    <Dialogo titulo="Tomar foto" ancho={560} alCerrar={alCerrar}>
      {error ? (
        <Aviso tono="error">{error}</Aviso>
      ) : (
        <>
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', maxHeight: '60vh', borderRadius: 8, background: '#000', display: 'block' }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="boton secundario" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="button" className="boton" onClick={capturar} disabled={!listo}>
              Capturar
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}

// ─── Escaneo de QR ──────────────────────────────────────────────────────────

/**
 * Linterna sobre el track de la cámara. Chrome de Android (y el WebView de la app) la enciende con
 * `applyConstraints({ torch })`. Devuelve `null` si el dispositivo no tiene linterna por esta vía.
 */
function linternaDelTrack(track: MediaStreamTrack | undefined): ((encender: boolean) => Promise<void>) | null {
  // `getCapabilities` no existe en todos los navegadores; sin él tampoco se puede saber si hay linterna.
  const capacidades = track?.getCapabilities?.() as (MediaTrackCapabilities & { torch?: boolean }) | undefined;
  if (!track || !capacidades?.torch) return null;
  return (encender) =>
    track.applyConstraints({ advanced: [{ torch: encender } as MediaTrackConstraintSet & { torch?: boolean }] });
}

/** Ancho al que se reduce cada cuadro antes de leerlo: con 720 px los QR lejanos o impresos chicos no se resuelven. */
const ANCHO_LECTURA = 1080;

/**
 * Solo QR, y «intentar a fondo»: sin esto zxing se rinde pronto con códigos impresos
 * en papel mate o con poco contraste, que otros lectores sí resuelven.
 */
const PISTAS_LECTURA = new Map<DecodeHintType, unknown>([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]],
  [DecodeHintType.TRY_HARDER, true],
]);

/** Invierte los colores (QR claro sobre fondo oscuro). El alfa no se toca. */
function invertirColores(imagen: ImageData): void {
  const { data } = imagen;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i]!;
    data[i + 1] = 255 - data[i + 1]!;
    data[i + 2] = 255 - data[i + 2]!;
  }
}

/**
 * Si el cuadro está oscuro (poca luz, superficie oscura), estira el contraste para que el QR
 * resalte. Sin linterna —o en un celular que no la expone— es lo único que puede ayudar a leerlo.
 * Usa percentiles 2 y 98 de la luminancia como puntos negro y blanco.
 */
function aclararSiOscuro(imagen: ImageData): void {
  const { data } = imagen;
  const histograma = new Uint32Array(256);
  let suma = 0;
  const pixeles = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    const luminancia = Math.round(0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!);
    histograma[luminancia]! += 1;
    suma += luminancia;
  }
  const media = suma / pixeles;
  if (media > 120) return;

  let acumulado = 0;
  let negro = 0;
  let blanco = 255;
  for (let v = 0; v < 256; v++) {
    acumulado += histograma[v]!;
    if (acumulado >= pixeles * 0.02 && negro === 0) negro = v;
    if (acumulado >= pixeles * 0.98) {
      blanco = v;
      break;
    }
  }
  const rango = Math.max(blanco - negro, 40);
  const escala = 255 / rango;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.min(255, Math.max(0, (data[i]! - negro) * escala));
    data[i + 1] = Math.min(255, Math.max(0, (data[i + 1]! - negro) * escala));
    data[i + 2] = Math.min(255, Math.max(0, (data[i + 2]! - negro) * escala));
  }
}

/**
 * Código de un QR leído. Las etiquetas de sede traen el enlace `…/s/<código>` y las de
 * equipo, el código a secas. Se toma el último segmento del enlace sin importar su largo:
 * hay códigos antiguos de 11 y 12 caracteres, y una regla de 13 los dejaba sin leer.
 */
function codigoDeLectura(texto: string): string {
  const limpio = texto.trim();
  const enlace = limpio.match(/\/(?:s|qr)\/([A-Za-z0-9]+)\/?(?:[?#].*)?$/);
  return (enlace?.[1] ?? limpio).toUpperCase();
}

/**
 * Pantalla completa que lee un QR con la cámara — igual que el escáner de
 * cualquier app nativa (WhatsApp, un lector de boletos, etc.): la cámara
 * ocupa todo el viewport, no un recorte adentro de un cuadro de diálogo, así
 * el técnico no tiene que acercar el celular más de la cuenta para que el
 * código entre en un visor chico. Si la cámara falla o la etiqueta está
 * gastada, deja transcribir el código a mano. Entrega el código ya
 * normalizado por `alDetectar`.
 */
export function EscanerQr({
  alDetectar,
  alCerrar,
  mostrarManual = true,
}: {
  alDetectar: (codigo: string) => void;
  alCerrar: () => void;
  /** En `false` omite el campo «O ingrese el código»: para cuando quien abre
   *  esta pantalla ya tiene su propio campo de captura manual (§16.2, página
   *  «Escanear QR»), y repetirlo acá sería el mismo control dos veces. */
  mostrarManual?: boolean;
}): JSX.Element {
  useCierreDialogo(alCerrar);
  const esMovil = useEsMovil();
  const videoRef = useRef<HTMLVideoElement>(null);
  const alDetectarRef = useRef(alDetectar);
  alDetectarRef.current = alDetectar;
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const [manualAbierto, setManualAbierto] = useState(false);
  const [linterna, setLinterna] = useState<{
    disponible: boolean;
    encendida: boolean;
    alternar: (encender: boolean) => void;
  } | null>(null);

  useEffect(() => {
    if (!hayCamara()) {
      setError('No hay cámara disponible. Ingrese el código manualmente.');
      return;
    }
    let cancelado = false;
    let flujo: MediaStream | null = null;
    let temporizador: number | undefined;
    // Leemos los cuadros nosotros (en vez de `decodeFromConstraints`) para poder
    // aclararlos antes de decodificar: con poca luz el QR no se lee aunque esté enfocado.
    const lector = new BrowserQRCodeReader(PISTAS_LECTURA, { delayBetweenScanAttempts: 75 });
    const lienzo = document.createElement('canvas');
    const contexto = lienzo.getContext('2d', { willReadFrequently: true });

    const detener = (): void => {
      cancelado = true;
      window.clearTimeout(temporizador);
      flujo?.getTracks().forEach((t) => t.stop());
    };

    /** Texto del QR en el lienzo, o `null` si en este cuadro no hay uno legible. */
    function leerLienzo(): string | null {
      try {
        return lector.decodeFromCanvas(lienzo).getText();
      } catch {
        return null;
      }
    }

    function leerCuadro(): void {
      if (cancelado) return;
      const video = videoRef.current;
      if (contexto && video && video.readyState >= 2 && video.videoWidth > 0) {
        const escala = Math.min(1, ANCHO_LECTURA / video.videoWidth);
        lienzo.width = Math.round(video.videoWidth * escala);
        lienzo.height = Math.round(video.videoHeight * escala);
        contexto.drawImage(video, 0, 0, lienzo.width, lienzo.height);
        const imagen = contexto.getImageData(0, 0, lienzo.width, lienzo.height);
        aclararSiOscuro(imagen);
        contexto.putImageData(imagen, 0, 0);
        let texto = leerLienzo();
        if (texto === null) {
          // Un QR claro sobre fondo oscuro no se lee en su orientación normal: se prueba invertido.
          invertirColores(imagen);
          contexto.putImageData(imagen, 0, 0);
          texto = leerLienzo();
        }
        if (texto !== null) {
          detener();
          alDetectarRef.current(codigoDeLectura(texto));
          return;
        }
      }
      temporizador = window.setTimeout(leerCuadro, 75);
    }

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      })
      .then(async (stream) => {
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        flujo = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const track = stream.getVideoTracks()[0];
        const alternar = linternaDelTrack(track);
        if (alternar) {
          setLinterna({
            disponible: true,
            encendida: false,
            alternar: (encender) => {
              alternar(encender)
                .then(() => setLinterna((l) => (l ? { ...l, encendida: encender } : l)))
                .catch(() => setLinterna((l) => (l ? { ...l, disponible: false } : l)));
            },
          });
        }
        leerCuadro();
      })
      .catch(() => {
        if (!cancelado) setError('No se pudo abrir la cámara. Ingrese el código manualmente.');
      });

    return detener;
  }, []);

  function enviarManual(): void {
    const codigo = manual.trim().toUpperCase();
    if (codigo) alDetectarRef.current(codigo);
  }

  const marco = error ? null : (
    // Marco guía: no acota la zona de lectura de zxing (sigue leyendo todo el
    // cuadro), pero centrar el QR ahí acerca el código al foco/nitidez de la
    // cámara y evita que quede cortado en el borde, que es lo que en la
    // práctica hace que tarde en leer.
    <div aria-hidden="true" className="pantalla-escaner-qr-marco">
      <div className="pantalla-escaner-qr-marco-sombra" />
      <div className="pantalla-escaner-qr-marco-borde" />
    </div>
  );

  const campoManual = (
    <div className="campo" style={{ marginTop: error ? 0 : 12 }}>
      <label htmlFor="qr-manual">O ingrese el código</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          id="qr-manual"
          value={manual}
          onChange={(e) => setManual(e.target.value.toUpperCase())}
          placeholder="Ej.: QNM0V66UVLMM4"
          maxLength={13}
          autoFocus={!!error}
          style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              enviarManual();
            }
          }}
        />
        <button type="button" className="boton secundario" onClick={enviarManual} disabled={!manual.trim()}>
          Usar
        </button>
      </div>
    </div>
  );

  // Móvil: pantalla completa, la cámara es lo primero que se ve (§ pedido).
  // Escritorio: rara vez hay una cámara apuntando de entrada, así que sigue
  // siendo un diálogo centrado de toda la vida — una toma completa de la
  // ventana ahí se siente invasiva, no como el escáner de una app nativa.
  if (esMovil) {
    return (
      <div className="pantalla-escaner-qr" role="dialog" aria-modal="true" aria-label="Escanear QR">
        <div className="pantalla-escaner-qr-visor">
          {error ? null : <video ref={videoRef} playsInline muted className="pantalla-escaner-qr-video" />}
          {marco}
        </div>

        <div className="pantalla-escaner-qr-cabecera">
          <button type="button" className="pantalla-escaner-qr-boton-icono" onClick={alCerrar} aria-label="Cerrar">
            <IconoCerrar />
          </button>
          <span>Escanear QR</span>
          {linterna?.disponible ? (
            <button
              type="button"
              className={`pantalla-escaner-qr-boton-icono${linterna.encendida ? ' activo' : ''}`}
              onClick={() => linterna.alternar(!linterna.encendida)}
              aria-label={linterna.encendida ? 'Apagar linterna' : 'Encender linterna'}
              aria-pressed={linterna.encendida}
            >
              <IconoLinterna />
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
        </div>

        <div className="pantalla-escaner-qr-pie">
          {error ? <Aviso tono="atencion">{error}</Aviso> : null}

          {/* Si la cámara falló, esta pantalla no tiene otra forma de capturar el
              código que este campo — se muestra igual aunque `mostrarManual` sea
              `false`. Sin error y con `mostrarManual`, queda detrás de un enlace:
              lo primero que se ve es la cámara, no un formulario. */}
          {mostrarManual || error ? (
            manualAbierto || error ? (
              campoManual
            ) : (
              <button type="button" className="pantalla-escaner-qr-enlace-manual" onClick={() => setManualAbierto(true)}>
                O ingresar el código a mano
              </button>
            )
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Dialogo titulo="Escanear QR" ancho={560} alCerrar={alCerrar}>
      {error ? (
        <Aviso tono="atencion">{error}</Aviso>
      ) : (
        <div style={{ position: 'relative', width: '100%', maxHeight: '55vh', borderRadius: 8, overflow: 'hidden' }}>
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', maxHeight: '55vh', background: '#000', display: 'block' }}
          />
          <div
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}
          >
            <div className="pantalla-escaner-qr-marco-sombra" style={{ gridArea: '1 / 1' }} />
            <div className="pantalla-escaner-qr-marco-borde" style={{ gridArea: '1 / 1' }} />
          </div>
          {linterna?.disponible ? (
            <button
              type="button"
              className={`pantalla-escaner-qr-boton-icono${linterna.encendida ? ' activo' : ''}`}
              onClick={() => linterna.alternar(!linterna.encendida)}
              aria-label={linterna.encendida ? 'Apagar linterna' : 'Encender linterna'}
              aria-pressed={linterna.encendida}
              style={{ position: 'absolute', top: 10, right: 10 }}
            >
              <IconoLinterna />
            </button>
          ) : null}
        </div>
      )}

      {/* Si la cámara falló, el diálogo no tiene otra forma de capturar el código
          que este campo — se muestra igual aunque `mostrarManual` sea `false`. */}
      {mostrarManual || error ? campoManual : null}
    </Dialogo>
  );
}

// ─── Resolución del código a una ficha ──────────────────────────────────────

export interface ResolucionQr {
  entidad: EntidadQR;
  entidadId: string;
  nombre: string;
  detalle: string | null;
  /** Ubicación de la entidad, para poder fijar ubicación y activo de una vez. */
  ubicacionId: string | null;
}

const ARTICULO_ENTIDAD: Record<EntidadQR, string> = {
  UBICACION: 'una ubicación',
  ACTIVO: 'un activo',
  PARTE: 'una parte',
};

/** Envuelve `GET /qr/:codigo` para no repetir la mutación en cada formulario. */
export function useResolverQr(
  alResuelto: (r: ResolucionQr) => void,
  /** `/qr` con sesión; `/publico/qr` en el formulario sin login. */
  rutaBase = '/qr',
): {
  resolver: (codigo: string) => void;
  resolviendo: boolean;
  error: string | null;
  limpiarError: () => void;
} {
  const [error, setError] = useState<string | null>(null);
  const mutacion = useMutation({
    mutationFn: (codigo: string) => {
      // `rutaBase` puede traer query (`?t=`): el código va antes y la query después.
      const [base, query] = rutaBase.split('?');
      return api.get<ResolucionQr>(
        `${base}/${encodeURIComponent(codigo.trim().toUpperCase())}${query ? `?${query}` : ''}`,
      );
    },
    onSuccess: (r) => {
      setError(null);
      alResuelto(r);
    },
    onError: (e: unknown) => {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo resolver el código.');
    },
  });
  return {
    resolver: mutacion.mutate,
    resolviendo: mutacion.isPending,
    error,
    limpiarError: () => setError(null),
  };
}

/**
 * Botón «Escanear QR» listo para pegar en un formulario: abre el escáner,
 * resuelve el código y entrega la ficha por `alResolver`. Si el código no es de
 * una entidad que el formulario admita (`entidades`), lo rechaza con un aviso en
 * vez de dejar el formulario a medio llenar.
 */
export function BotonEscanearQr({
  etiqueta = 'Escanear QR',
  entidades,
  alResolver,
  className = 'boton-adjunto',
  mostrarTexto = false,
  rutaResolver,
}: {
  etiqueta?: string;
  entidades?: EntidadQR[];
  alResolver: (r: ResolucionQr) => void;
  className?: string;
  /** Icono + texto visible, en vez del ícono solo que usan los formularios. */
  mostrarTexto?: boolean;
  /** Base de la ruta que resuelve el código; por defecto la que exige sesión. */
  rutaResolver?: string;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const { resolver, resolviendo, error, limpiarError } = useResolverQr((r) => {
    if (entidades && !entidades.includes(r.entidad)) {
      setAviso(`El código corresponde a ${ARTICULO_ENTIDAD[r.entidad]} y no sirve para este formulario.`);
      return;
    }
    setAviso(null);
    setAbierto(false);
    alResolver(r);
  }, rutaResolver);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <button
        type="button"
        className={className}
        title={resolviendo ? 'Resolviendo…' : etiqueta}
        aria-label={resolviendo ? 'Resolviendo…' : etiqueta}
        onClick={() => {
          setAviso(null);
          limpiarError();
          setAbierto(true);
        }}
        disabled={resolviendo}
        style={mostrarTexto ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}
      >
        <IconoQr />
        {mostrarTexto ? (resolviendo ? 'Resolviendo…' : etiqueta) : null}
      </button>
      {aviso ?? error ? <span className="campo-error">{aviso ?? error}</span> : null}
      {abierto ? <EscanerQr alCerrar={() => setAbierto(false)} alDetectar={(codigo) => resolver(codigo)} /> : null}
    </div>
  );
}
